import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  UserCheck,
  Search,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X,
  Clock,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import TrainerFormDialog from "./TrainerFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminTrainersSection({
  onBack,
  initialFilter = "all",
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [showForm, setShowForm] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [deletingTrainer, setDeletingTrainer] = useState(null);

  const { data: trainers = [], isLoading } = useQuery({
    queryKey: ["admin_trainers"],
    queryFn: () => base44.entities.Trainer.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Trainer.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_trainers"] });
      setDeletingTrainer(null);
    },
  });

  const filteredTrainers = useMemo(() => {
    return trainers.filter((t) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (t.name && t.name.toLowerCase().includes(q)) ||
        (t.email && t.email.toLowerCase().includes(q)) ||
        (t.specialization && t.specialization.toLowerCase().includes(q));

      if (!matchSearch) return false;

      const isActive = t.is_active !== false;
      if (filter === "active") return isActive;
      if (filter === "pending") return !isActive;
      return true;
    });
  }, [trainers, search, filter]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
            aria-label="Back to Command Center"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Trainers</h1>
            <p className="text-[11px] text-slate-400">
              {filteredTrainers.length} of {trainers.length} trainers
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingTrainer(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition-all cursor-pointer"
          data-testid="add-trainer-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Trainer</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or specialization…"
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-[#071326] border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Status Filter Horizontal Pills */}
      <div className="flex items-center gap-1.5">
        {[
          { id: "all", label: "All" },
          { id: "active", label: "Active" },
          { id: "pending", label: "Pending Approval" },
        ].map((pill) => {
          const isActive = filter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setFilter(pill.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? "bg-sky-500 text-slate-950 shadow-sm"
                  : "bg-white/5 text-slate-400 hover:text-white border border-white/5"
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Trainer Cards List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filteredTrainers.length === 0 ? (
        <div className="rounded-2xl p-8 bg-[#071326] border border-white/10 text-center space-y-2">
          <UserCheck className="w-8 h-8 text-slate-500 mx-auto" />
          <p className="text-xs font-bold text-white">No trainers found</p>
          <p className="text-[11px] text-slate-400">
            {search ? "No trainers match your search." : "No records in this category."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTrainers.map((trainer) => {
            const isActive = trainer.is_active !== false;
            return (
              <div
                key={trainer.id}
                className="p-3 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition-all flex items-center justify-between gap-3 shadow-sm"
                data-testid={`trainer-card-${trainer.id}`}
              >
                {/* Left: Avatar + Details */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center text-emerald-400 font-bold text-xs shrink-0 overflow-hidden">
                    {trainer.photo_url ? (
                      <img
                        src={trainer.photo_url}
                        alt={trainer.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      (trainer.name?.[0] || "?").toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-white truncate">{trainer.name}</p>
                      <span
                        className={`px-1.5 py-0.2 rounded-full text-[9px] font-bold ${
                          isActive
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-amber-500/20 text-amber-300"
                        }`}
                      >
                        {isActive ? "Active" : "Pending"}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 truncate">{trainer.email}</p>
                    {trainer.specialization && (
                      <p className="text-[10px] text-sky-400 font-medium truncate mt-0.5">
                        {trainer.specialization}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTrainer(trainer);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition-all cursor-pointer"
                    aria-label="Edit Trainer"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletingTrainer(trainer)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-white/10 active:scale-95 transition-all cursor-pointer"
                    aria-label="Delete Trainer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      {showForm && (
        <TrainerFormDialog
          trainer={editingTrainer}
          onClose={() => {
            setShowForm(false);
            setEditingTrainer(null);
          }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["admin_trainers"] });
            setShowForm(false);
            setEditingTrainer(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingTrainer && (
        <AlertDialog open onOpenChange={() => setDeletingTrainer(null)}>
          <AlertDialogContent className="bg-[#030A17] border border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Trainer</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300 text-xs">
                Are you sure you want to remove{" "}
                <span className="font-semibold text-white">{deletingTrainer.name}</span>? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate(deletingTrainer.id)}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
